"use client"

import { useCallback, useState } from "react"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Lock, StickyNote, Users, Pin } from "lucide-react"
import { useLocalizedField } from "@/utils/language-utils"
import { useViewManager } from "../view-manager-context"
import { SkeletonRows } from "./skeleton-rows"

// ─── Sortable Column Header ─────────────────────────────────────────

function SortableColumnHeader({ id, field, onSelect, isSelected, disabled }: {
  id: string
  field: any
  onSelect: (fieldId: string) => void
  isSelected: boolean
  disabled?: boolean
}) {
  const getLocalizedField = useLocalizedField()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  const [showGrab, setShowGrab] = useState(false)

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : 1,
    outline: isDragging ? '2px solid #3B82F6' : isSelected ? '2px solid #0F214B' : undefined,
    minWidth: 140,
    maxWidth: 220,
    position: 'relative',
    userSelect: 'none',
  }

  const title = getLocalizedField(field, 'columnName') || field.columnCode || id

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-[#0F214B] bg-[#F9F9FF] cursor-pointer whitespace-nowrap"
      onClick={() => onSelect(id)}
      onMouseEnter={() => !disabled && setShowGrab(true)}
      onMouseLeave={() => !disabled && setShowGrab(false)}
    >
      {/* Grab handle */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          style={{
            position: 'absolute',
            top: showGrab || isDragging ? 0 : -6,
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: showGrab || isDragging ? 1 : 0,
            pointerEvents: showGrab || isDragging ? 'auto' : 'none',
            transition: 'opacity 0.15s ease, top 0.15s ease',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 12,
            borderRadius: '0 0 5px 5px',
            background: isDragging ? '#3B82F6' : '#ddd',
            cursor: 'grab',
          }}
          title="Drag to reorder"
        >
          <GripVertical size={9} style={{ color: isDragging ? '#fff' : '#888' }} />
        </div>
      )}

      {/* Title + property icons */}
      <div className="flex items-center gap-1 truncate">
        <span className="truncate">{title}</span>
        {disabled && <Pin size={10} className="text-gray-400 flex-shrink-0" />}
        {field.isPrivate && <Lock size={10} className="text-gray-400 flex-shrink-0" />}
        {field.displayNote && <StickyNote size={10} className="text-gray-400 flex-shrink-0" />}
        {field.isEnabledForClients && <Users size={10} className="text-amber-500 flex-shrink-0" />}
      </div>
    </th>
  )
}

// ─── Table Preview Component ─────────────────────────────────────────

interface TablePreviewProps {
  /** When a column header is clicked, this fires with the fieldConfigurationId */
  onColumnSelect?: (fieldId: string) => void
  /** Which column is currently selected (highlighted) */
  selectedColumnId?: string | null
}

export function TablePreview({ onColumnSelect, selectedColumnId }: TablePreviewProps) {
  const { state, dispatch } = useViewManager()
  const stageId = state.activeEditStage
  const columnIds = state.draftStageColumns[stageId] || []

  // Look up field configurations from the loaded config
  const fieldsInUse = state.originalConfig?.fieldsInUse || []
  const stageConfig = (state.originalConfig?.stageConfigurations || []).find(
    (s: any) => (s.stageId || s.stageCode) === stageId
  )
  const stageFields = stageConfig?.fieldConfigurations || []

  // Build a map of all available fields
  const fieldMap = new Map<string, any>()
  for (const f of fieldsInUse) fieldMap.set(f.fieldConfigurationId, f)
  for (const f of stageFields) fieldMap.set(f.fieldConfigurationId, f)

  // Identify the Message column — it must always be first
  const messageFieldId = columnIds.find(id => {
    const f = fieldMap.get(id)
    return f?.columnCode === 'Message' || f?.columnCode === 'message'
  })

  // Split into fixed (Message) + reorderable columns
  const fixedIds = messageFieldId ? [messageFieldId] : []
  const reorderableIds = columnIds.filter(id => id !== messageFieldId)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    // Only reorder among non-fixed columns
    const oldIndex = reorderableIds.indexOf(active.id as string)
    const newIndex = reorderableIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const newReorderable = arrayMove(reorderableIds, oldIndex, newIndex)
    dispatch({ type: 'REORDER_COLUMNS', payload: { stageId, fieldIds: [...fixedIds, ...newReorderable] } })
  }, [reorderableIds, fixedIds, stageId, dispatch])

  const handleColumnClick = useCallback((fieldId: string) => {
    onColumnSelect?.(fieldId)
  }, [onColumnSelect])

  if (columnIds.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-gray-400 border rounded-lg bg-gray-50">
        No columns configured for this stage
      </div>
    )
  }

  return (
    <div>
      {/* Legend — above the table */}
      <div className="flex items-center gap-4 px-4 py-1.5 text-[10px] text-gray-500 mb-1">
        <span className="font-medium text-gray-600">Legend:</span>
        <span className="flex items-center gap-1"><Lock size={10} className="text-gray-400" /> Private</span>
        <span className="flex items-center gap-1"><StickyNote size={10} className="text-gray-400" /> Has Note</span>
        <span className="flex items-center gap-1"><Users size={10} className="text-amber-500" /> Client Visible</span>
        <span className="flex items-center gap-1"><Pin size={10} className="text-gray-400" /> Fixed Position</span>
      </div>
      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToHorizontalAxis]}
        >
          <table className="w-full" style={{ tableLayout: 'fixed', minWidth: columnIds.length * 160 }}>
            <thead>
              <tr>
                {/* Fixed columns (Message) — not draggable */}
                {fixedIds.map(fieldId => {
                  const field = fieldMap.get(fieldId) || { columnCode: fieldId }
                  return (
                    <SortableColumnHeader
                      key={fieldId}
                      id={fieldId}
                      field={field}
                      onSelect={handleColumnClick}
                      isSelected={selectedColumnId === fieldId}
                      disabled={true}
                    />
                  )
                })}
                {/* Reorderable columns */}
                <SortableContext items={reorderableIds} strategy={horizontalListSortingStrategy}>
                  {reorderableIds.map(fieldId => {
                    const field = fieldMap.get(fieldId) || { columnCode: fieldId }
                    return (
                      <SortableColumnHeader
                        key={fieldId}
                        id={fieldId}
                        field={field}
                        onSelect={handleColumnClick}
                        isSelected={selectedColumnId === fieldId}
                      />
                    )
                  })}
                </SortableContext>
              </tr>
            </thead>
            <SkeletonRows columnCount={columnIds.length} />
          </table>
        </DndContext>
      </div>
      </div>
    </div>
  )
}
